import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { create } from 'zustand';
import { useQuery } from 'react-query';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import ConsumptionView from './consumptionView';
import TargetsView from './targetsView';
import { PageTitle, PageLinks } from '../Headings';
import { getMillInit } from '../../services/measurement';
import UnsavedChangesModal from './unsavedChangesModal';
import { useReportDetailTabsStore } from '../../pages/ReportDetailPage';
import { PermissionInfo } from '../../services/model';
import { getUserPermissions } from '../../services/acl';
import SourcesView from './sourcesView';
import { TabName } from '../Excel/constants';

// export const CATEGORY_FILTER_ITEMS = [
//   { name: 'Electricity', value: 'electricity' },
//   { name: 'Heat', value: 'heat' },
// ];

const TopPanel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;

  ${PageTitle} {
    a {
      font-weight: normal;
    }
  }
`;

type ReportDetailState = {
  updatedCells: any[];
  resultToast: any;
  userPermissions: PermissionInfo;
  setUpdatedCells: (data: any[]) => void;
  hasUnsavedData: () => boolean;
  setResultToast: (element: any) => void;
  setUserPermissions: (data: PermissionInfo) => void;
};

export const useReportDetailStore = create<ReportDetailState>((set, get) => ({
  updatedCells: [],
  resultToast: null,
  userPermissions: {
    canAccessReport: false,
    canUpdateFigures: false,
    canSubmitTarget: false,
    canApproveTarget: false,
    canApproveDivisionalTarget: false,
  },
  setUpdatedCells: (data: any[]) => set(() => ({ updatedCells: data })),
  hasUnsavedData: () => get().updatedCells.length > 0,
  setResultToast: (element: any) => set(() => ({ resultToast: element })),
  setUserPermissions: (data: PermissionInfo) => set(() => ({ userPermissions: data })),
}));

function ReportDetail({ placeCode }: { placeCode: string }) {
  const [reasonsData, setReasonsData] = useState([]);
  const [sourcesData, setSourcesData] = useState([]);
  const [nextTabIndex, setNextTabIndex] = useState<number>(-1);
  const { activeTabIndex, setActiveTabIndex } = useReportDetailTabsStore();
  const { setUpdatedCells, hasUnsavedData, setResultToast, setUserPermissions } = useReportDetailStore();

  const resultToast = useRef<Toast>(null);
  const messages = useRef<Messages>(null);

  const {
    data: initData,
    isFetching: fetchingInitData,
    error: initError,
  } = useQuery(['millInit', placeCode, activeTabIndex], () => getMillInit(placeCode, getActiveTabName(activeTabIndex)));

  const { isLoading: loadingUserPermissions, error: permissionsError } = useQuery(
    ['userPermission', placeCode],
    () => getUserPermissions(placeCode),
    {
      onSuccess: (data) => setUserPermissions(data),
    },
  );

  const getActiveTabName = (index: number): TabName => {
    switch (index) {
      case 0:
        return TabName.Consumption;
      case 1:
        return TabName.Targets;
      case 2:
        return TabName.Sources;
      default:
        return 'Unknown' as TabName;
    }
  };

  const activeTabName = getActiveTabName(activeTabIndex);

  useEffect(() => {
    if (initData) {
      setSourcesData(initData.valueTypes.filter((v) => v.isProduction));
      setReasonsData(initData.reasons);
    }
  }, [initData]);

  useEffect(() => {
    if (initError || permissionsError) {
      messages.current?.show({ severity: 'error', summary: 'An error has occurred while loading data!', sticky: true });
    }
  }, [initError, permissionsError]);

  useEffect(() => {
    if (resultToast) {
      setResultToast(resultToast);
    }
  }, []);

  return (
    <>
      <Toast ref={resultToast} />
      <UnsavedChangesModal
        isOpen={nextTabIndex > -1}
        onCancel={() => setNextTabIndex(-1)}
        onConfirm={() => {
          setUpdatedCells([]);
          setActiveTabIndex(nextTabIndex || 0);
          setNextTabIndex(-1);
        }}
      />
      <Messages ref={messages} />
      {(fetchingInitData || loadingUserPermissions) && (
        <ProgressBar mode="indeterminate" style={{ height: '4px', marginBottom: '16px' }} />
      )}
      <TopPanel>
        <PageTitle>
          {initData && (
            <div>
              <span>{`${initData.millData.name}, ${initData.millData.code}`}</span>{' '}
            </div>
          )}
        </PageTitle>
        <div>
          <>
            {initData && (initData?.millData?.plosAppUrl || initData?.millData?.plosPBIUrl) && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', textAlign: 'left' }}>
                  <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>Quick links:</span>
                  {initData.millData.plosAppUrl && (
                    <PageLinks href={initData.millData.plosAppUrl} target="_blank" rel="noopener noreferrer">
                      PLOS Energy App
                    </PageLinks>
                  )}
                  {initData.millData.plosPBIUrl && (
                    <PageLinks href={initData.millData.plosPBIUrl} target="_blank" rel="noopener noreferrer">
                      PLOS PBI Report
                    </PageLinks>
                  )}
                </div>
              </>
            )}
          </>
        </div>
      </TopPanel>
      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
        onBeforeTabChange={(e) => {
          if (hasUnsavedData()) {
            setNextTabIndex(e.index);
            return false;
          }
          return true;
        }}
      >
        <TabPanel header="Consumption">
          <ConsumptionView placeCode={placeCode} reasonsData={reasonsData} tabName={activeTabName} />
        </TabPanel>
        <TabPanel header="Targets">
          <TargetsView placeCode={placeCode} reasonsData={reasonsData} tabName={activeTabName} />
        </TabPanel>

        <TabPanel header="Sources">
          <SourcesView
            placeCode={placeCode}
            sourcesData={sourcesData}
            reasonsData={reasonsData}
            tabName={activeTabName}
          />
        </TabPanel>
      </TabView>
    </>
  );
}

export default ReportDetail;
